import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@vibrent/electryon';
import { createKeycloakAdapter } from '../../utils/keycloakAdapter';
import {
  getAuthenticatedKeycloakTokens,
  normalizeKeycloakConfig,
  shouldSkipUserInfoSync,
} from '../../utils/authFlow';
import applyUserLocalePreferences from '../../utils/applyUserLocalePreferences';
import * as spinnerStyle from '../logout/style.scss';
import * as actions from './actions';
import * as userActions from '../../actions/userActions';

const Login = (props) => {
  const { location: { search },
    actions: { getHelpCentreDetails },
    history, setUserDetails, userDetail, keyCloakConfig,
    isCheckingSession, isLoggedIn } = props;
  const { i18n } = useTranslation();
  const [error, setError] = useState(null);
  const loadedUserKeyRef = useRef(null);
  const loginFlowStarted = useRef(false);

  const syncKeycloakSession = (accessToken, idToken, refreshToken) => {
    if (shouldSkipUserInfoSync(accessToken)) {
      return;
    }
    const decodedURL = decodeURIComponent(search);
    const newURL = decodedURL.replaceAll('?', '&');
    const URLSearch = new URLSearchParams(newURL);
    props.actions.getUserInfoDetails(
      accessToken,
      idToken,
      refreshToken,
      URLSearch.get('program'),
    );
  };

  useEffect(() => {
    if (!keyCloakConfig) {
      props.actions.getKeyCloakURL();
    }
  }, []);

  useEffect(() => {
    if (!keyCloakConfig || isLoggedIn || loginFlowStarted.current) {
      return;
    }

    const config = normalizeKeycloakConfig(keyCloakConfig);
    if (!config) {
      return;
    }

    loginFlowStarted.current = true;

    const existingTokens = getAuthenticatedKeycloakTokens();
    if (existingTokens) {
      syncKeycloakSession(
        existingTokens.accessToken,
        existingTokens.idToken,
        existingTokens.refreshToken,
      );
      return;
    }

    try {
      const adapter = createKeycloakAdapter(config);
      window.keycloakAuth = adapter.getInstance && adapter.getInstance();

      adapter.init({
        onLoad: 'login-required',
        flow: 'standard',
      }).then((authenticated) => {
        if (authenticated && adapter.isAuthenticated()) {
          window.keycloakAuth = adapter.getInstance();
          syncKeycloakSession(
            adapter.getToken(),
            adapter.getIdToken(),
            adapter.getRefreshToken(),
          );
        }
      }).catch((err) => {
        loginFlowStarted.current = false;
        let errorMsg = 'Authentication initialization failed';
        if (err) {
          if (err instanceof Error) {
            errorMsg = err.message || 'Authentication initialization failed';
          } else if (typeof err === 'string') {
            errorMsg = err;
          } else if (err.message) {
            errorMsg = err.message;
          } else if (err.errorMessage) {
            errorMsg = err.errorMessage;
          }
        }
        setError({ errorMessage: errorMsg });
      });
    } catch (configError) {
      loginFlowStarted.current = false;
      setError({ errorMessage: 'Invalid Keycloak configuration' });
    }
  }, [keyCloakConfig, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || _.isEmpty(userDetail)) {
      return;
    }
    const userKey = userDetail.username || userDetail.id;
    if (loadedUserKeyRef.current === userKey) {
      return;
    }
    loadedUserKeyRef.current = userKey;
    if (userDetail.jwtToken && setUserDetails) {
      getHelpCentreDetails(userDetail.jwtToken, true);
    }
    applyUserLocalePreferences(userDetail, i18n);
  }, [userDetail, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    const redirect = (new URLSearchParams(search)).get('redirect');
    history.replace(redirect || '/');
  }, [isLoggedIn, history, search]);

  useEffect(() => {
    if (props.error && !_.isEqual(error, props.error.message)) {
      setError({ error: props.error.message });
    }
  }, [props.error]);

  return isCheckingSession ? (
    <div className={spinnerStyle['log-out-spinner-container']}>
      <Spinner />
    </div>
  ) : null;
};

const mapStateToProps = state => ({
  isCheckingSession: state.LoginReducer.get('isCheckingSession'),
  error: state.LoginReducer.get('error'),
  keyCloakConfig: state.LoginReducer.get('keyCloakConfig'),
  userDetail: state.UserDetail.get('userDetail') || state.LoginReducer.get('userDetail'),
  isLoggedIn: state.UserDetail.get('isLoggedIn'),
  setUserDetails: state.LoginReducer.get('setUserDetails'),
});

const mapDispatchToProps = dispatch => ({
  actions:
    bindActionCreators(
      Object.assign({}, actions, userActions),
      dispatch,
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Login);

export const LoginComponent = Login;

Login.propTypes = {
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    getHelpCentreDetails: PropTypes.func.isRequired,
    getKeyCloakURL: PropTypes.func.isRequired,
    getUserInfoDetails: PropTypes.func.isRequired,
    updateJwtToken: PropTypes.func.isRequired,
  }).isRequired,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  keyCloakConfig: PropTypes.shape({
    resource: PropTypes.string,
    realm: PropTypes.string,
    url: PropTypes.string,
    credentials: PropTypes.shape({
      secret: PropTypes.string,
    }),
  }),
  setUserDetails: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool,
  isCheckingSession: PropTypes.bool.isRequired,
  userDetail: PropTypes.shape({
    preferredLanguage: PropTypes.string.isRequired,
    username: PropTypes.string,
    id: PropTypes.string,
    jwtToken: PropTypes.string,
  }),
};

Login.defaultProps = {
  error: null,
  keyCloakConfig: null,
  userDetail: null,
  setUserDetails: false,
  isLoggedIn: false,
  getProgramDetails: false,
};
